import type { BookingStatus, SiteClosure } from '@/src/lib/hospitality/wire';

/**
 * The decisions the booking block makes, as functions with no React in them.
 *
 * <p>Everything here is either a formatting rule tied to the *channel's* time
 * zone or a mapping from a backend answer to a string this app owns. Both are
 * worth asserting and neither needs a DOM, which is why they are not inlined
 * into the component: the app's vitest setup is `environment: 'node'`, so a test
 * that had to render would not run at all.
 *
 * <h3>Nothing here computes availability</h3>
 *
 * <p>Hours, closures, capacity, combinability and turn length belong to the
 * availability engine. The one closure decision below ({@link closureOn}) is not
 * an availability computation — it reads the closure list the site descriptor
 * already published so the diner is told *why* a date is dead, instead of being
 * shown an empty grid they will read as "fully booked". A slot the API did not
 * return is never offered, and no slot is ever synthesised.
 */

/**
 * Today — or any instant — as the local date in a time zone, `YYYY-MM-DD`.
 *
 * <p>The diner picks a calendar date; the restaurant's diary is kept in the
 * channel's zone. Using the browser's zone would offer a visitor in Berlin a
 * different "today" from the one the kitchen is working, and the mistake only
 * shows up either side of midnight, in one direction, for some visitors.
 *
 * <p>Built from `formatToParts` rather than a locale that happens to print ISO:
 * `en-CA` gives `2027-01-01` today and is not contractually obliged to tomorrow.
 * Returns an empty string when the zone is unset or unknown — the caller then
 * has no date to pre-select, which is visible, rather than a date silently taken
 * from the server's own zone.
 */
export function localDateIn(timeZone: string, at: Date = new Date()): string {
  if (!timeZone) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(at);
    const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    const year = pick('year');
    const month = pick('month');
    const day = pick('day');
    return year && month && day ? `${year}-${month}-${day}` : '';
  } catch {
    return '';
  }
}

/** `YYYY-MM-DD` plus whole days, in the calendar and never in a zone. Used only
 *  to bound the date picker, so a diner cannot ask for a date in the past or a
 *  year out. */
export function addDays(date: string, days: number): string {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parsed) return date;
  const at = new Date(Date.UTC(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3])));
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * A slot's start as a clock time in the CHANNEL's zone.
 *
 * <p>`startAt` is an instant. The zone that turns it into "19:30" is the
 * restaurant's, not the browser's and not the server's — a confirmation that
 * reads 16:30 because the render happened in New York has told the diner the
 * wrong time to turn up. Explicit `timeZone` is what makes the process's own
 * `TZ` irrelevant.
 */
export function formatTimeIn(iso: string, timeZone: string, locale: string): string {
  return formatIn(iso, timeZone, locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** The full "when" of a booking — weekday, date and clock time — in the
 *  channel's zone, in the visitor's language. */
export function formatDateTimeIn(iso: string, timeZone: string, locale: string): string {
  return formatIn(iso, timeZone, locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatIn(
  iso: string,
  timeZone: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(at);
  } catch {
    // An unknown zone must not take the page down. The instant is still shown,
    // unformatted, rather than replaced by a time in the wrong zone.
    return iso;
  }
}

/**
 * The closure covering a date, or null.
 *
 * <p>Matched by exact string equality on the ISO local date the site published.
 * `reason` is handed back untouched: a Turkish operator's `Yılbaşı` renders as
 * `Yılbaşı` — never transliterated, never translated, never title-cased.
 */
export function closureOn(closures: SiteClosure[], date: string): SiteClosure | null {
  if (!date) return null;
  return closures.find((closure) => closure.closedOn === date) ?? null;
}

/**
 * Which of this app's strings answers a backend refusal, or null to fall back to
 * the envelope's own `message`.
 *
 * <p>Only the refusals a diner can act on are given words here. Two of them
 * matter enough to be worth stating why:
 *
 * <ul>
 *   <li>`BOOKING_DEPOSIT_REQUIRED` — the channel takes a deposit and this site
 *       has no payment step to take it with. The block renders the blocked
 *       state and tells the diner to phone; it never synthesises an amount,
 *       which would write a `Payment` for money nobody has collected.</li>
 *   <li>`BOOKING_DURATION_REQUIRED` — the operator has configured no turn time.
 *       The page asks the diner to phone rather than guessing one.</li>
 * </ul>
 *
 * <p>Anything else — including a code shipped after this build — falls through
 * to `null` and the diner reads the contract's own `message`. That is why the
 * backend's wording is carried across the BFF at all: an unknown refusal must
 * still say something true.
 */
export function refusalStringKey(errorKey: string | null | undefined): string | null {
  if (!errorKey) return null;
  switch (errorKey) {
    case 'BOOKING_RESOURCE_TAKEN':
      return 'hospitality.booking.refusal.taken';
    case 'BOOKING_DEPOSIT_REQUIRED':
      return 'hospitality.booking.refusal.deposit';
    case 'BOOKING_DURATION_REQUIRED':
      return 'hospitality.booking.refusal.duration';
    case 'BOOKING_PARTY_TOO_LARGE':
    case 'BOOKING_NO_RESOURCE_FITS':
    case 'BOOKING_NOT_COMBINABLE':
      return 'hospitality.booking.refusal.partyTooLarge';
    case 'BOOKING_PARTY_TOO_SMALL':
      return 'hospitality.booking.refusal.partyTooSmall';
    case 'BOOKING_OUTSIDE_HOURS':
    case 'BOOKING_LOCATION_CLOSED':
      return 'hospitality.booking.refusal.closed';
    default:
      return null;
  }
}

/**
 * Whether a refusal means "the slot went while you were typing".
 *
 * <p>Its own function because it drives behaviour, not just wording: the grid is
 * re-fetched and the diner is told plainly what happened, rather than shown a
 * generic error next to a time that no longer exists.
 */
export function isSlotTaken(errorKey: string | null | undefined): boolean {
  return errorKey === 'BOOKING_RESOURCE_TAKEN';
}

/**
 * Whether a refusal is the deposit gap.
 *
 * <p>There is no public payment endpoint for a diner today, so a deposit-taking
 * channel simply cannot be booked from this site. That is rendered as the
 * brief's blocked state — a named, permanent "phone us", not a retry button
 * that will fail identically every time.
 */
export function isDepositBlocked(errorKey: string | null | undefined): boolean {
  return errorKey === 'BOOKING_DEPOSIT_REQUIRED';
}

/**
 * The wording a created booking gets: the channel decided, not this screen.
 *
 * <p>`BookingService.create` reads `booking.requireConfirmation` off the
 * channel's config and picks `REQUESTED` or `CONFIRMED`. A site that vets its
 * web bookings must not tell a diner their table is held, and one that does not
 * must not make them wait for a call that will never come — so the wording comes
 * from the status on the response and from nothing else.
 */
export function bookingStatusStringKey(status: BookingStatus | null): string {
  switch (status) {
    case 'CONFIRMED':
    case 'SEATED':
    case 'COMPLETED':
      return 'hospitality.booking.status.confirmed';
    case 'REQUESTED':
      return 'hospitality.booking.status.requested';
    case 'WAITLISTED':
      return 'hospitality.booking.status.waitlisted';
    case 'CANCELLED':
      return 'hospitality.booking.status.cancelled';
    case 'NO_SHOW':
      return 'hospitality.booking.status.noShow';
    default:
      // A status this build does not know is never rendered as "confirmed".
      return 'hospitality.booking.status.unknown';
  }
}

/** The `Booking.note` column is 2000 characters. */
export const BOOKING_NOTE_MAX = 2000;

export interface BookingFormValues {
  date: string;
  covers: number;
  startAt: string;
  name: string;
  phone: string;
  email: string;
  note: string;
}

/**
 * The fields the request cannot be made without, as string keys.
 *
 * <p>Deliberately shallow: a name, a phone number, a party size and a slot the
 * server offered. Nothing here judges whether the restaurant can seat the party
 * or whether the time is inside the diary — the engine owns both, and a browser
 * that decided either would eventually disagree with it.
 */
export function bookingFormErrors(values: BookingFormValues): string[] {
  const errors: string[] = [];
  if (!values.startAt) errors.push('hospitality.booking.validation.slot');
  if (!Number.isInteger(values.covers) || values.covers < 1) {
    errors.push('hospitality.booking.validation.covers');
  }
  if (!values.name.trim()) errors.push('hospitality.booking.validation.name');
  if (!values.phone.trim()) errors.push('hospitality.booking.validation.phone');
  if (values.note.length > BOOKING_NOTE_MAX) {
    errors.push('hospitality.booking.validation.note');
  }
  return errors;
}

/**
 * A fresh idempotency key for one submission attempt.
 *
 * <p>`Booking` has no `clientRef` column: this string, claimed as an
 * `ApiIdempotencyKey`, is the whole of what stops a form retried over a flaky
 * connection from seating the same party twice. It is minted **once per
 * submission attempt** and reused for that attempt's retries — a new key per
 * network call is indistinguishable from having none.
 *
 * <p>`crypto.randomUUID` is absent on an insecure origin in some browsers, so
 * there is a fallback; it is not cryptographically stronger than it needs to be,
 * because the key is a deduplication token and not a secret.
 */
export function newIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
